import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e27] via-[#1a1f3a] to-[#0a0e27] text-white relative overflow-hidden">
      {/* Starry Background */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(100)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.7 + 0.3,
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#0a0e27]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] bg-clip-text text-transparent">온니잡</span>
          </div>
          <div className="flex items-center gap-8">
            <a href="#types" className="text-sm hover:text-[#d4af37] transition-colors whitespace-nowrap cursor-pointer">유형별 정보</a>
            <a href="#guide" className="text-sm hover:text-[#d4af37] transition-colors whitespace-nowrap cursor-pointer">시작 가이드</a>
            <a href="#faq" className="text-sm hover:text-[#d4af37] transition-colors whitespace-nowrap cursor-pointer">자주 묻는 질문</a>
            <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] text-[#0a0e27] rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all whitespace-nowrap cursor-pointer">상담하기</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-[#d4af37]/20 border border-[#d4af37] rounded-full text-[#d4af37] text-sm mb-8">
            밤하늘처럼 빛나는 기회
          </div>
          <h1 className="text-7xl font-light mb-4">유흥알바, 이제는</h1>
          <h2 className="text-7xl font-bold bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] bg-clip-text text-transparent mb-8">
            온니잡으로 시작하세요
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            체계적인 정보 정리부터 실제 근무 환경까지, 처음 알아보는 분들도 명확하게 이해할 수 있는 프리미엄 정보 플랫폼
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] text-[#0a0e27] rounded-full text-lg font-bold hover:shadow-2xl hover:shadow-[#d4af37]/50 transition-all cursor-pointer whitespace-nowrap">
              지금 시작하기 →
            </a>
            <a href="https://onnyjob.onelink.me/z1EO/ussr8zbl?deep_link_value=eyJhZ2VuY3kiOiJBRzAyMjcyNjAwNTIifQ==" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-full text-lg font-bold hover:bg-white/20 hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-smartphone-line text-xl text-[#d4af37]"></i>
              </div>
              앱 다운로드
            </a>
          </div>

          <div className="mt-20 relative">
            <div className="rounded-3xl overflow-hidden relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/8162919bb29a23292cfd80bacf138ee6.jpg" 
                alt="별이 빛나는 밤하늘" 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40 flex items-center justify-center">
                <p className="text-3xl font-semibold">신뢰할 수 있는 정보, 명확한 선택</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Split Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-2 gap-0">
          <div className="bg-gradient-to-br from-[#1a1f3a] to-[#2d1b4e] p-16 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>
            <div className="relative z-10">
              <p className="text-sm text-gray-400 mb-12">WHY ONNI JOB</p>
              <h3 className="text-5xl font-bold mb-4 text-white">밤알바도</h3>
              <h3 className="text-6xl font-bold mb-4 text-[#d4af37]">룸알바도</h3>
              <h3 className="text-7xl font-bold text-white">온니잡이 정답</h3>
            </div>
          </div>

          <div className="bg-white text-[#0a0e27] p-16 flex flex-col justify-center">
            <h3 className="text-5xl font-bold mb-4">처음이라면</h3>
            <h3 className="text-5xl font-bold mb-8">더욱 온니잡</h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-10">
              유형별 차이를 이해하지 못하면 시행착오가 반복됩니다. 온니잡은 텐프로, 일프로, 쩜오, 룸알바, 클럽까지 각 환경의 특징과 근무 방식을 명확히 정리해 드립니다. 고수익 여성알바를 찾는다면, 조건만이 아닌 전체 흐름을 이해하는 것이 먼저입니다.
            </p>
            <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="inline-block self-center px-8 py-3 bg-[#3d2817] text-white rounded-full font-semibold hover:bg-[#2d1b0f] transition-colors cursor-pointer whitespace-nowrap">
              상세 정보 보기 →
            </a>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section id="intro" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">유흥알바 소개</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              단순한 구인 정보 나열이 아니라, 처음 알아보는 분들도 전체 구조와 흐름을 이해할 수 있도록 정리된 정보 안내 방식입니다
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
            <h3 className="text-2xl font-bold mb-8 text-[#d4af37]">알아보기 전, 꼭 짚어야 할 포인트</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-2xl text-[#d4af37]"></i>
                </div>
                <p className="text-lg">근무 형태와 환경 차이를 이해했는가?</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-2xl text-[#d4af37]"></i>
                </div>
                <p className="text-lg">본인 상황에 맞는 선택인가?</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-2xl text-[#d4af37]"></i>
                </div>
                <p className="text-lg">전체적인 시스템과 흐름이 정리되어 있는가?</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types Comparison Section */}
      <section id="types" className="relative py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">유형별 비교</h2>
          
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#d4af37] to-[#f4e5a1]">
                    <th className="px-8 py-5 text-left text-[#0a0e27] font-bold text-lg">구분</th>
                    <th className="px-8 py-5 text-left text-[#0a0e27] font-bold text-lg">특징</th>
                    <th className="px-8 py-5 text-left text-[#0a0e27] font-bold text-lg">추천 대상</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="px-8 py-6 text-[#0a0e27] font-semibold text-lg">텐프로</td>
                    <td className="px-8 py-6 text-gray-700">응대 방식과 근무 기준이 정형화됨</td>
                    <td className="px-8 py-6 text-gray-700">체계적인 환경 선호</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-8 py-6 text-[#0a0e27] font-semibold text-lg">룸알바</td>
                    <td className="px-8 py-6 text-gray-700">부담 낮춘 중간 단계 근무</td>
                    <td className="px-8 py-6 text-gray-700">유흥알바 초보</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-8 py-6 text-[#0a0e27] font-semibold text-lg">일프로 / 쩜오</td>
                    <td className="px-8 py-6 text-gray-700">공간 중심, 응대 중심</td>
                    <td className="px-8 py-6 text-gray-700">차분한 환경 선호</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-8 py-6 text-[#0a0e27] font-semibold text-lg">클럽 / 라운지</td>
                    <td className="px-8 py-6 text-gray-700">오픈된 공간, 활동적</td>
                    <td className="px-8 py-6 text-gray-700">자유롭고 활동적 선호</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* High Income Section */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">고수익 여성알바를 위한 중요한 조건</h2>
          
          <div className="grid grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-5xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">전체 구조 이해</h3>
              <p className="text-gray-300 leading-relaxed">
                업종별 요구 기준과 근무 방식이 다르므로 전체 구조를 먼저 이해해야 함
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-time-line text-5xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">차이점 파악</h3>
              <p className="text-gray-300 leading-relaxed">
                근무 시간, 분위기, 응대 방식 등 차이를 알고 선택할수록 시행착오 감소
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-5xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">지속 가능성</h3>
              <p className="text-gray-300 leading-relaxed">
                단기 조건보다 지속 가능한 환경과 기준을 우선 판단
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Selection Criteria Section */}
      <section id="guide" className="relative py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-8 text-center">선택 기준 정리</h2>
          <p className="text-xl text-gray-300 text-center mb-16 max-w-3xl mx-auto">
            유흥알바는 다양한 형태가 존재하므로 핵심만 정리해 판단하는 것이 중요합니다
          </p>

          <div className="grid grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#1a1f3a] to-[#2d1b4e] rounded-2xl p-8 border border-[#d4af37]/30">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-[#d4af37]/20 rounded-full">
                <i className="ri-user-heart-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">본인 상황 확인</h3>
              <p className="text-gray-300 leading-relaxed">
                본인 상황과 맞는 근무 형태인지
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#1a1f3a] to-[#2d1b4e] rounded-2xl p-8 border border-[#d4af37]/30">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-[#d4af37]/20 rounded-full">
                <i className="ri-building-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">환경 이해</h3>
              <p className="text-gray-300 leading-relaxed">
                근무 환경과 분위기에 대한 이해
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#1a1f3a] to-[#2d1b4e] rounded-2xl p-8 border border-[#d4af37]/30">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-[#d4af37]/20 rounded-full">
                <i className="ri-file-list-3-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">조건 일치</h3>
              <p className="text-gray-300 leading-relaxed">
                설명된 조건과 실제 흐름의 일치 여부
              </p>
            </div>
          </div>

          <div className="mt-16 bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
            <h3 className="text-2xl font-bold mb-6 text-[#d4af37]">정보 접근 방식과 체감 변화</h3>
            <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
              <p>
                같은 정보를 보더라도 어떤 기준으로 정리해 접근하느냐에 따라 체감과 만족도에 차이가 생김
              </p>
              <p>
                기준 없이 탐색하면 정보가 무의미해질 수 있으나, 흐름과 조건을 이해하면 선택이 명확해짐
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onni Job Introduction */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] rounded-3xl p-16 text-[#0a0e27] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/20 rounded-full -ml-24 -mb-24"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-[#0a0e27] rounded-full">
                  <i className="ri-star-fill text-3xl text-[#d4af37]"></i>
                </div>
                <h2 className="text-5xl font-bold">온니잡 소개</h2>
              </div>
              
              <p className="text-2xl font-semibold mb-8">
                유흥알바는 온니잡으로 하는게 짱이다! ✨
              </p>
              
              <div className="grid grid-cols-2 gap-8 mb-10">
                <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center mb-4 bg-[#0a0e27] rounded-full">
                    <i className="ri-check-double-line text-2xl text-[#d4af37]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">체계적인 정보 정리</h3>
                  <p className="text-[#0a0e27]/80">
                    텐프로부터 클럽까지 모든 유형을 명확하게 비교하고 정리
                  </p>
                </div>

                <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center mb-4 bg-[#0a0e27] rounded-full">
                    <i className="ri-shield-star-line text-2xl text-[#d4af37]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">신뢰할 수 있는 플랫폼</h3>
                  <p className="text-[#0a0e27]/80">
                    검증된 정보만을 제공하는 프리미엄 정보 플랫폼
                  </p>
                </div>

                <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center mb-4 bg-[#0a0e27] rounded-full">
                    <i className="ri-user-smile-line text-2xl text-[#d4af37]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">초보자도 쉽게</h3>
                  <p className="text-[#0a0e27]/80">
                    처음 알아보는 분들도 전체 흐름을 쉽게 이해
                  </p>
                </div>

                <div className="bg-white/30 backdrop-blur-sm rounded-2xl p-6">
                  <div className="w-12 h-12 flex items-center justify-center mb-4 bg-[#0a0e27] rounded-full">
                    <i className="ri-customer-service-2-line text-2xl text-[#d4af37]"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">전문 상담 지원</h3>
                  <p className="text-[#0a0e27]/80">
                    궁금한 점은 언제든지 전문 상담을 통해 해결
                  </p>
                </div>
              </div>

              <div className="text-center">
                <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-4 bg-[#0a0e27] text-white rounded-full text-lg font-bold hover:bg-[#1a1f3a] transition-all cursor-pointer whitespace-nowrap">
                  온니잡 시작하기 →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">자주 묻는 질문 (FAQ)</h2>
          
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#d4af37] rounded-full">
                  <span className="text-[#0a0e27] font-bold">Q</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-3">유흥알바를 처음 알아보는 사람도 시작할 수 있나요?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    네, 가능합니다. 중요한 것은 근무 형태 및 환경을 이해하고 선택하는 과정입니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#d4af37] rounded-full">
                  <span className="text-[#0a0e27] font-bold">Q</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-3">밤알바와 룸알바는 어떻게 구분해야 하나요?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    근무 시간대, 공간 구조, 응대 방식 등에 따라 차이가 생기기 때문에 단순 수익 비교보다 근무 흐름 기준으로 판단하는 것이 좋습니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#d4af37] rounded-full">
                  <span className="text-[#0a0e27] font-bold">Q</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-3">고수익 여성알바는 누구에게 적합한가요?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    모든 사람에게 동일하게 적합하다고 보긴 어렵습니다. 근무 조건과 리듬이 본인 상황과 맞을 때 현실적인 선택이 될 수 있습니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#d4af37] transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#d4af37] rounded-full">
                  <span className="text-[#0a0e27] font-bold">Q</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-3">정보를 볼 때 주의할 점은 무엇인가요?</h3>
                  <p className="text-gray-300 leading-relaxed">
                    조건 설명만 보고 결정하기보다는 근무 환경과 실제 흐름이 함께 설명되어 있는지를 확인하는 것이 중요합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-8">지금 바로 시작하세요</h2>
          <p className="text-xl text-gray-300 mb-12">
            온니잡과 함께 명확한 정보로 올바른 선택을 하세요
          </p>
          <div className="flex items-center justify-center gap-6">
            <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="px-10 py-4 bg-gradient-to-r from-[#d4af37] to-[#f4e5a1] text-[#0a0e27] rounded-full text-lg font-bold hover:shadow-2xl hover:shadow-[#d4af37]/50 transition-all cursor-pointer whitespace-nowrap">
              상담 신청하기
            </a>
            <a href="https://onnyjob.kr/" target="_blank" rel="noopener noreferrer" className="px-10 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-lg font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
              더 알아보기
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative mt-20 mx-6 mb-6">
        <div className="bg-gradient-to-br from-[#1a1f3a] to-[#0a0e27] rounded-3xl p-16 border border-white/10 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-30">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 grid grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-3xl font-bold mb-6 leading-tight">온니잡과 함께<br />시작하세요</h3>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">정보</h4>
              <div className="space-y-3">
                <a href="#types" className="block text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">유형별 비교</a>
                <a href="#guide" className="block text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">선택 기준</a>
                <a href="#faq" className="block text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">FAQ</a>
                <a href="#intro" className="block text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">이용 후기</a>
                <a href="#contact" className="block text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">상담 신청</a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">소셜</h4>
              <div className="space-y-3">
                <a href="https://www.instagram.com/onnyjob__official/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                  <span>인스타그램</span>
                </a>
                <a href="#" className="flex items-center gap-3 text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-youtube-line text-xl"></i>
                  <span>유튜브</span>
                </a>
                <a href="https://pf.kakao.com/_xgsPfn/chat" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-kakao-talk-line text-xl"></i>
                  <span>카카오톡</span>
                </a>
              </div>
            </div>

            <div>
              <p className="text-lg mb-4">최신 정보를 받아보세요</p>
              <div className="flex items-center gap-2">
                <input 
                  type="email" 
                  placeholder="이메일 주소" 
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:border-[#d4af37]"
                />
                <button className="w-12 h-12 flex items-center justify-center bg-white rounded-full hover:bg-[#d4af37] transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-xl text-[#0a0e27]"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="relative z-10 pt-8 border-t border-white/10">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">© 2024 온니잡. All rights reserved.</p>
              <div className="flex items-center gap-2">
                <img src="https://static.readdy.ai/image/5e8897340d9d3c287a609e9913af4db2/c9be56f561a78a029d60568564f6bf09.jpeg" alt="온니잡" className="w-8 h-8 rounded-lg object-cover" />
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[120px] font-bold text-white/5 whitespace-nowrap pointer-events-none">
            ONNI JOB
          </div>
        </div>
      </footer>

      {/* KakaoTalk Floating Button */}
      <a
        href="https://pf.kakao.com/_xgsPfn/chat"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 z-50 w-16 h-16 flex items-center justify-center bg-[#FEE500] rounded-full shadow-2xl hover:scale-110 transition-all duration-300 cursor-pointer"
        style={{ boxShadow: '0 4px 24px rgba(254,229,0,0.5)' }}
        title="카카오톡 상담"
      >
        <i className="ri-kakao-talk-fill text-3xl text-[#3A1D1D]"></i>
      </a>
    </div>
  );
}